import { useEffect } from "react";
import { List, Mail, ShieldCheck } from "lucide-react";
import SECTIONS from "./privacyContent";

function Block({ b }) {
  if (b.t === "h") {
    return <h3 className="font-sans font-bold text-navy mt-7 mb-2">{b.x}</h3>;
  }
  if (b.t === "ul") {
    return (
      <ul className="my-3 space-y-1.5">
        {b.items.map((it, i) => (
          <li key={i} className="flex items-start gap-2.5 text-clay leading-relaxed">
            <span className="w-1.5 h-1.5 rounded-full bg-gold mt-2 shrink-0" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    );
  }
  return <p className="my-3 text-clay leading-relaxed">{b.x}</p>;
}

export default function PrivacyPolicy() {
  useEffect(() => {
    document.title = "Privacy Policy — BurmeseHub";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-cream min-h-screen" data-testid="privacy-policy-page">
      {/* Header band */}
      <section className="bg-navy acheik-bg-dark text-white py-14 lg:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <p className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.25em] text-gold mb-4">
            <ShieldCheck className="w-4 h-4" /> Legal
          </p>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" data-testid="privacy-title">
            Privacy Policy
          </h1>
          <p className="mt-4 text-white/70 text-sm sm:text-base" data-testid="privacy-effective-date">
            BurmeseHub.com · Effective Date: October 23, 2026
          </p>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 lg:py-16">
        {/* Table of contents */}
        <nav className="bg-white border border-[#E2E8F0] rounded-2xl p-6 sm:p-8 mb-12 shadow-ambient" data-testid="privacy-toc">
          <p className="flex items-center gap-2 font-sans font-bold text-navy mb-4">
            <List className="w-4 h-4 text-gold-dark" /> On this page
          </p>
          <ol className="grid sm:grid-cols-2 gap-x-8 gap-y-2 list-decimal list-inside">
            {SECTIONS.map((s, i) => (
              <li key={s.id} className="text-sm">
                <a
                  href={`#${s.id}`}
                  data-testid={`privacy-toc-${s.id}`}
                  className="text-navy-royal hover:text-gold-dark transition-colors font-medium"
                >
                  {s.title}
                </a>
              </li>
            ))}
            <li className="text-sm">
              <a href="#contact" data-testid="privacy-toc-contact" className="text-navy-royal hover:text-gold-dark transition-colors font-medium">
                Contact Information
              </a>
            </li>
          </ol>
        </nav>

        {/* Sections */}
        <article className="space-y-12">
          {SECTIONS.map((s, i) => (
            <section key={s.id} id={s.id} className="scroll-mt-28" data-testid={`privacy-section-${s.id}`}>
              <h2 className="font-serif text-2xl sm:text-3xl font-bold text-navy tracking-tight pb-3 mb-4 border-b border-[#E2E8F0]">
                <span className="text-gold-dark font-mono text-lg mr-3">{i + 1}.</span>
                {s.title}
              </h2>
              {s.blocks.map((b, j) => <Block key={j} b={b} />)}
            </section>
          ))}

          {/* Contact */}
          <section id="contact" className="scroll-mt-28" data-testid="privacy-section-contact">
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-navy tracking-tight pb-3 mb-4 border-b border-[#E2E8F0]">
              <span className="text-gold-dark font-mono text-lg mr-3">{SECTIONS.length + 1}.</span>
              Contact Information
            </h2>
            <p className="my-3 text-clay leading-relaxed">
              If you have questions about this Privacy Policy, wish to exercise your privacy rights, or need assistance with your business listing, please contact us.
            </p>
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 my-5 space-y-3">
              <p className="font-sans font-bold text-navy">BurmeseHub.com</p>
              <p className="text-clay text-sm">
                Website: <a href="https://www.burmesehub.com" className="text-navy-royal font-semibold hover:text-gold-dark" data-testid="privacy-contact-website">https://www.burmesehub.com</a>
              </p>
              <p className="text-clay text-sm flex items-center gap-2">
                <Mail className="w-4 h-4 text-gold-dark" />
                Email: <a href="mailto:admin@burmesehub.com" className="text-navy-royal font-semibold hover:text-gold-dark" data-testid="privacy-contact-email">admin@burmesehub.com</a>
              </p>
              <p className="text-clay text-sm">
                Privacy Requests: <a href="mailto:admin@burmesehub.com?subject=Privacy%20Request" className="text-navy-royal font-semibold hover:text-gold-dark" data-testid="privacy-request-link">Contact us by email</a> with the subject line &ldquo;Privacy Request.&rdquo;
              </p>
              <p className="text-clay text-sm">
                Business Listing Requests: <a href="mailto:admin@burmesehub.com?subject=Business%20Listing%20Update%20or%20Removal" className="text-navy-royal font-semibold hover:text-gold-dark" data-testid="listing-removal-link">Contact us</a> with the subject line &ldquo;Business Listing Update or Removal.&rdquo;
              </p>
            </div>

            <h3 className="font-serif text-xl font-bold text-navy mt-10 mb-3">Our Commitment to Your Privacy</h3>
            <p className="my-3 text-clay leading-relaxed">
              At BurmeseHub, our goal is to bring communities closer together while respecting the privacy of every individual and business we serve.
            </p>
            <p className="my-3 text-clay leading-relaxed">
              We believe trust, transparency, and responsible information practices are essential to building a stronger and more connected Burmese community.
            </p>
            <p className="mt-8 font-serif italic text-xl text-gold-dark">BurmeseHub.com — Find. Connect. Belong.</p>
          </section>
        </article>
      </div>
    </div>
  );
}
